import { Component, Injectable, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Broker } from '../../model/Broker';
import { BrokerService } from '../../services/broker.service';
import { CountryService } from '../../services/country.service';
import { UrlService } from "../../../shared/services/url.service";
import { UrlHelperService } from "../../../shared/services/urlHelperService";
import { AuthService } from "../../../authentication/services/auth.service";
import { AlertService } from "../../../shared/alert/services/alert.service";
import { AuthorService } from "../../../shared/services/author.service";
import { FormGroup } from '@angular/forms';
import { LocalDataSource } from 'ng2-smart-table';
import {
  initEmptyModel,
  mapToCountryTagModelArray,
  mapToTagModelArray,
  mapToBannedCountryTagModelArray,
  mapToStringArray
} from './broker-model-utils';
import {
  accountTypeTableSettings,
  promotionTableSettings,
  educationProgramTableSettings,
  confirmNameNotEmpty,
  onMoveAccountTypes,
  onMovePromotions,
  onMoveEducationPrograms
} from './broker-edit-tables-config';
import { Author, AuthorMini } from '../../../shared/models/Author';

@Component({
  selector: 'app-broker',
  templateUrl: '../templates/broker.component.html',
  styleUrls: ['./broker.css']
})
@Injectable()
export class BrokerComponent implements OnInit {
  title = 'User';
  DashboardUrl = '';
  BrokerId: string;
  CurrentLanguage: string;
  IsEdition: boolean = false;
  Broker: Broker;
  Platforms: any[];
  AccountCurrencies: any[];
  Languages: any[];
  LotSizes: any[];
  PaymentMethods: any[];
  Instruments: any[];
  Regulations: any[];
  BusinessNatures: any[];
  BannedCountries: any[];
  AutocompleteCountries: any[];
  Authors: Author[];
  AccountTypesData: LocalDataSource = new LocalDataSource();
  PromotionsData: LocalDataSource = new LocalDataSource();
  EducationProgramsData: LocalDataSource = new LocalDataSource();
  SelectedAuthor: Author;

  constructor(private activatedRoute: ActivatedRoute,
    private brokerService: BrokerService,
    private countryService: CountryService,
    private authService: AuthService,
    private alertService: AlertService,
    private urlService: UrlService,
    private authorService: AuthorService,
    private urlHelperService: UrlHelperService) { }

  ngOnInit() {
    this.DashboardUrl = this.urlService.GetDashboardRoute();
    this.BrokerId = this.activatedRoute.snapshot.params.brokerId;
    this.CurrentLanguage = this.authService.getActualLanguage().cultureNameISO;
    this.authorService.GetAuthorsByContentType(8).subscribe(authors => {
      this.Authors = authors;
    });
    this.initModel();
  }

  IsLoaded(): boolean {
    return typeof this.Broker !== 'undefined';
  }

  validateBrokerType(): boolean {
    return this.Broker && (this.Broker.isForex || this.Broker.isCrypto);
  }

  Save(form: FormGroup) {
    this.alertService.removeAll();
    window.scrollTo(0, 0);
    if (!form.valid) {
      this.alertService.add('danger', 'There are some errors in the form.');
      return;
    }

    if (!this.validateBrokerType()) {
      this.alertService.add('danger', 'At least one broker type (Forex or Crypto) must be selected.');
      return;
    }

    this.mapModel().then(broker => {
      if (this.brokerService.ValidateBroker(broker)) {
        if (this.BrokerId) {
          this.Edit(broker);
        } else {
          this.Create(broker);
        }

      }
    });
  };

  Create(broker: Broker) {
    this.brokerService.CreateBroker(this.CurrentLanguage, broker)
      .subscribe(() => this.createSuccess(), err => this.alertService.add('danger', 'Create Broker error: ' + err));
  };

  Edit(broker: Broker) {
    this.brokerService.EditBroker(this.CurrentLanguage, broker)
      .subscribe(() => this.editSuccess(), err => this.alertService.add('danger', 'Edit Broker error: ' + err));
  };

  AddAllCountriesAsBanned() {
    this.BannedCountries = this.AutocompleteCountries;
  };

  RemoveAllCountriesAsBanned() {
    this.BannedCountries = [];
  };

  private initModel() {
    this.countryService.GetCountries().subscribe(countries => {
      this.AutocompleteCountries = mapToCountryTagModelArray(countries);

      if (this.BrokerId) {
        this.brokerService.GetBrokerById(this.CurrentLanguage, this.BrokerId)
          .subscribe(data => this.fillModel(data)
            , err => this.alertService.add('danger', 'Get brokerById error: ' + err));
      } else {
        this.Broker = initEmptyModel();
      }
    });
  }

  private fillModel(broker: Broker) {
    if (this.BrokerId) {
      this.Broker = broker;
      this.IsEdition = true;
      this.Platforms = mapToTagModelArray(broker.platforms);
      this.AccountCurrencies = mapToTagModelArray(broker.accountCurrencies);
      this.Languages = mapToTagModelArray(broker.languages);
      this.LotSizes = mapToTagModelArray(broker.lotSizes);
      this.PaymentMethods = mapToTagModelArray(broker.paymentMethods);
      this.Instruments = mapToTagModelArray(broker.instruments);
      this.Regulations = mapToTagModelArray(broker.regulations);
      this.BusinessNatures = mapToTagModelArray(broker.businessNature);
      this.BannedCountries = mapToBannedCountryTagModelArray(broker.bannedCountries, this.AutocompleteCountries);
      this.AccountTypesData = new LocalDataSource(broker.accountTypes);
      this.PromotionsData = new LocalDataSource(broker.promotions);
      this.EducationProgramsData = new LocalDataSource(broker.educationPrograms);
      if (broker.authorId) {
        this.SelectedAuthor = this.Authors.find((x) => x.AuthorId === broker.authorId);
      }
    }
  }

  private mapModel(): Promise<Broker> {
    var brokerModel = this.Broker;
    brokerModel.platforms = mapToStringArray(this.Platforms);
    brokerModel.accountCurrencies = mapToStringArray(this.AccountCurrencies);
    brokerModel.languages = mapToStringArray(this.Languages);
    brokerModel.lotSizes = mapToStringArray(this.LotSizes);
    brokerModel.paymentMethods = mapToStringArray(this.PaymentMethods);
    brokerModel.instruments = mapToStringArray(this.Instruments);
    brokerModel.regulations = mapToStringArray(this.Regulations);
    brokerModel.businessNature = mapToStringArray(this.BusinessNatures);
    brokerModel.bannedCountries = mapToStringArray(this.BannedCountries);
    brokerModel.authorId = this.SelectedAuthor ? this.SelectedAuthor.AuthorId : undefined;

    var promise = new Promise<Broker>((resolve, reject) => Promise.all([
      this.AccountTypesData.getAll(),
      this.PromotionsData.getAll(),
      this.EducationProgramsData.getAll()
    ]).then(([accountTypes, promotions, educationPrograms]) => {
      brokerModel.accountTypes = accountTypes;
      brokerModel.promotions = promotions.map(promo => {
        if (promo.isActive === "")
          promo.isActive = false;
        return promo;
      });
      brokerModel.educationPrograms = educationPrograms;
      resolve(this.Broker);
    }).catch(error => reject(error))
    );

    return promise;
  }

  public generateUrl() {
    if (!this.IsEdition && this.Broker.name.length !== 0)
      this.Broker.url = this.urlHelperService.generateUrl(this.Broker.name);
  }

  private createSuccess() {
    this.alertService.add('success', 'Broker successfully created');
  };

  private editSuccess() {
    this.alertService.add('success', 'Broker has been edited.');
  };

  accountTypeTableSettings = accountTypeTableSettings;
  promotionTableSettings = promotionTableSettings;
  educationProgramTableSettings = educationProgramTableSettings;
  confirmNameNotEmpty = confirmNameNotEmpty;
  onMoveAccountTypes = onMoveAccountTypes;
  onMovePromotions = onMovePromotions;
  onMoveEducationPrograms = onMoveEducationPrograms;
};



// WEBPACK FOOTER //
// ./src/app/brokers/components/scripts/broker.component.ts